import { useState, type FormEvent } from 'react';
import { Navigate } from 'react-router-dom';
import { useAccesAdmin } from './acces';
import { adminFetch, apiErrorMessage, errorMessage } from './adminFetch';
import './AdminComptesPage.css';

type Statut = 'PENDING' | 'ACTIVE' | 'SUSPENDED' | 'DELETED';

interface Compte {
  id: string;
  email: string;
  nom: string | null;
  statut: Statut;
  adresseVerifiee: boolean;
  superadmin: boolean;
  support: boolean;
  supprime: boolean;
  creeLe: string;
  derniereConnexion: string | null;
  sessionsOuvertes: number;
  espace: { nom: string; role: 'OWNER' | 'ADMIN' | 'MEMBER'; dossiers: Dossier[] } | null;
  action: 'suspendre' | 'reactiver' | null;
  blocage: string | null;
}

/** Dossier de l'espace, avec l'option « SIRET de test SUPER PDP » (10/10/2026). */
interface Dossier {
  id: string;
  nom: string;
  siret: string | null;
  siretTest: boolean;
}

interface Resultat {
  comptes: Compte[];
  tronque: boolean;
}

const DATE = new Intl.DateTimeFormat('fr-FR', { dateStyle: 'medium', timeStyle: 'short' });

const JSON_ENTETES = { 'Content-Type': 'application/json' };

const STATUTS: Record<Statut, { libelle: string; classe: string }> = {
  ACTIVE: { libelle: 'Actif', classe: 'actif' },
  PENDING: { libelle: 'Adresse non confirmée', classe: 'attente' },
  SUSPENDED: { libelle: 'Suspendu', classe: 'suspendu' },
  DELETED: { libelle: 'Supprimé', classe: 'supprime' },
};

const ROLES: Record<NonNullable<Compte['espace']>['role'], string> = {
  OWNER: 'propriétaire',
  ADMIN: 'administrateur',
  MEMBER: 'membre',
};

/**
 * Comptes clients de la suite : recherche, suspension, réactivation
 * (04/10/2026, ZO-AUTH-06).
 *
 * Réservé au superadmin, comme l'écran Équipe : suspendre un compte le coupe
 * de TOUS les produits. Les refus (son propre compte, un compte de l'équipe,
 * un compte supprimé) viennent du serveur, qui les applique de toute façon ;
 * l'écran se contente de les afficher à la place du bouton.
 *
 * Pas de chargement au montage : on ne liste pas l'annuaire, on cherche.
 */
export function AdminComptesPage() {
  const acces = useAccesAdmin();
  const [resultat, setResultat] = useState<Resultat | null>(null);
  const [derniereRecherche, setDerniereRecherche] = useState('');
  const [erreur, setErreur] = useState<string | null>(null);
  const [avis, setAvis] = useState<string | null>(null);
  const [occupe, setOccupe] = useState<string | null>(null);

  if (!acces.superadmin) return <Navigate to="/admin/tickets" replace />;

  async function lancer(texte: string) {
    setErreur(null);
    setOccupe('recherche');
    try {
      const res = await adminFetch(`/admin/comptes?q=${encodeURIComponent(texte)}`);
      if (!res.ok) throw new Error(await apiErrorMessage(res));
      setResultat((await res.json()) as Resultat);
      setDerniereRecherche(texte);
    } catch (err) {
      setErreur(errorMessage(err));
    } finally {
      setOccupe(null);
    }
  }

  function rechercher(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setAvis(null);
    const texte = String(new FormData(event.currentTarget).get('q') ?? '').trim();
    if (texte) void lancer(texte);
  }

  async function agir(compte: Compte, action: 'suspendre' | 'reactiver') {
    // La saisie du motif VAUT confirmation : « Annuler » n'envoie rien. Le
    // motif lui-même est facultatif, il ne sert qu'au journal d'audit.
    const motif = window.prompt(
      action === 'suspendre'
        ? `Suspendre ${compte.email} ?\n\nToutes ses sessions sont fermées et ses produits cessent de renouveler leurs jetons (au plus tard 15 minutes pour un produit déjà ouvert).\n\nMotif (facultatif, journalisé) :`
        : `Réactiver ${compte.email} ?\n\nLa personne devra se reconnecter : ses anciennes sessions ne reviennent pas.\n\nMotif (facultatif, journalisé) :`,
      '',
    );
    if (motif === null) return;

    setErreur(null);
    setAvis(null);
    setOccupe(compte.id);
    try {
      const res = await adminFetch(`/admin/comptes/${compte.id}/${action}`, {
        method: 'POST',
        headers: JSON_ENTETES,
        body: JSON.stringify(motif.trim() ? { motif: motif.trim() } : {}),
      });
      if (!res.ok) throw new Error(await apiErrorMessage(res));
      const maj = (await res.json()) as Compte & { sessionsFermees?: number; jetonsRevoques?: number };
      setResultat((actuel) =>
        actuel ? { ...actuel, comptes: actuel.comptes.map((c) => (c.id === maj.id ? maj : c)) } : actuel,
      );
      setAvis(
        action === 'suspendre'
          ? `${maj.email} est suspendu : ${maj.sessionsFermees ?? 0} session(s) fermée(s), ${maj.jetonsRevoques ?? 0} jeton(s) de renouvellement révoqué(s).`
          : `${maj.email} est réactivé (${STATUTS[maj.statut].libelle.toLowerCase()}).`,
      );
    } catch (err) {
      setErreur(errorMessage(err));
    } finally {
      setOccupe(null);
    }
  }

  /**
   * Option « SIRET de test SUPER PDP » d'un dossier : accepte, pour lui et ses
   * clients, les SIRET fictifs 00000000100001 et 00000000200001. Les produits
   * la voient au prochain renouvellement du jeton (15 minutes au plus).
   */
  async function basculerSiretTest(compte: Compte, dossier: Dossier) {
    const actif = !dossier.siretTest;
    setErreur(null);
    setAvis(null);
    setOccupe(dossier.id);
    try {
      const res = await adminFetch(`/admin/comptes/dossiers/${dossier.id}/siret-test`, {
        method: 'POST',
        headers: JSON_ENTETES,
        body: JSON.stringify({ actif }),
      });
      if (!res.ok) throw new Error(await apiErrorMessage(res));
      const maj = (await res.json()) as Dossier;
      setResultat((actuel) =>
        actuel
          ? {
              ...actuel,
              comptes: actuel.comptes.map((c) =>
                c.id === compte.id && c.espace
                  ? {
                      ...c,
                      espace: {
                        ...c.espace,
                        dossiers: c.espace.dossiers.map((d) => (d.id === maj.id ? maj : d)),
                      },
                    }
                  : c,
              ),
            }
          : actuel,
      );
      setAvis(
        actif
          ? `« ${maj.nom} » accepte désormais les SIRET de test SUPER PDP (effectif dans les produits sous 15 minutes).`
          : `« ${maj.nom} » n’accepte plus les SIRET de test SUPER PDP.`,
      );
    } catch (err) {
      setErreur(errorMessage(err));
    } finally {
      setOccupe(null);
    }
  }

  return (
    <div className="adm-page">
      <h1>Comptes clients</h1>
      <p className="adm-sub">
        Rechercher un compte du portail par adresse e-mail, nom ou identifiant, et le suspendre. Une
        suspension ferme <strong>immédiatement</strong> toutes ses sessions et révoque ses jetons de
        renouvellement : la personne ne peut plus se connecter à aucun outil de la suite. Chaque
        suspension et chaque réactivation est inscrite au journal d’audit.
      </p>

      {erreur && (
        <p className="adm-erreur" role="alert">
          {erreur}
        </p>
      )}
      {avis && (
        <p className="adm-comptes-avis" role="status">
          {avis}
        </p>
      )}

      <form className="adm-ajout" onSubmit={rechercher} role="search">
        <label>
          Adresse e-mail, nom ou identifiant
          <input name="q" type="search" required minLength={2} maxLength={200} placeholder="dupont, jean@exemple.fr…" />
        </label>
        <button className="btn" type="submit" disabled={occupe === 'recherche'}>
          {occupe === 'recherche' ? 'Recherche…' : 'Rechercher'}
        </button>
      </form>
      <p className="adm-hint">
        Les comptes de l’équipe (superadmins, agents support) ne se suspendent pas ici : retirez
        d’abord leurs statuts dans l’écran Équipe.
      </p>

      {resultat === null ? null : resultat.comptes.length === 0 ? (
        <p>Aucun compte ne correspond à « {derniereRecherche} ».</p>
      ) : (
        <>
          {resultat.tronque && (
            <p className="adm-alerte">Seuls les premiers résultats sont affichés : affinez la recherche.</p>
          )}
          <table className="adm-table">
            <thead>
              <tr>
                <th>Compte</th>
                <th>Statut</th>
                <th>Espace</th>
                <th>Sessions ouvertes</th>
                <th>Dernière connexion</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {resultat.comptes.map((c) => (
                <tr key={c.id}>
                  <td>
                    <strong>{c.email}</strong>
                    {c.nom ? <span className="adm-muted"> — {c.nom}</span> : null}
                    {c.email === acces.email ? <span className="adm-muted"> (vous)</span> : null}
                    {c.superadmin ? <span className="adm-comptes-etiquette">Superadmin</span> : null}
                    {c.support ? <span className="adm-comptes-etiquette">Agent support</span> : null}
                    <div className="adm-muted adm-comptes-id">
                      {c.id} · créé le {DATE.format(new Date(c.creeLe))}
                    </div>
                  </td>
                  <td>
                    <span className={`adm-comptes-pastille adm-comptes-pastille--${STATUTS[c.statut].classe}`}>
                      {STATUTS[c.statut].libelle}
                    </span>
                  </td>
                  <td>
                    {c.espace ? `${c.espace.nom} (${ROLES[c.espace.role]})` : '—'}
                    {c.espace && c.espace.dossiers.length > 0 ? (
                      <ul className="adm-comptes-dossiers">
                        {c.espace.dossiers.map((d) => (
                          <li key={d.id}>
                            <span>
                              {d.nom}
                              {d.siret ? <span className="adm-muted"> · {d.siret}</span> : null}
                            </span>
                            <label className="adm-comptes-siret-test">
                              <input
                                type="checkbox"
                                checked={d.siretTest}
                                disabled={occupe === d.id}
                                onChange={() => void basculerSiretTest(c, d)}
                              />
                              SIRET de test SUPER PDP
                            </label>
                          </li>
                        ))}
                      </ul>
                    ) : null}
                  </td>
                  <td>{c.sessionsOuvertes}</td>
                  <td>{c.derniereConnexion ? DATE.format(new Date(c.derniereConnexion)) : '—'}</td>
                  <td className="adm-comptes-action">
                    {c.action === 'suspendre' ? (
                      <button
                        className="btn btn-danger btn-small"
                        type="button"
                        disabled={occupe === c.id}
                        onClick={() => void agir(c, 'suspendre')}
                      >
                        Suspendre
                      </button>
                    ) : c.action === 'reactiver' ? (
                      <button
                        className="btn btn-secondary btn-small"
                        type="button"
                        disabled={occupe === c.id}
                        onClick={() => void agir(c, 'reactiver')}
                      >
                        Réactiver
                      </button>
                    ) : (
                      <span className="adm-muted">{c.blocage ?? '—'}</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </div>
  );
}
